import React, { useState, useEffect } from 'react';
import { useDragAndDrop } from '@formkit/drag-and-drop/react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import {
  Sparkles, Smartphone, Save, Trash2, Wrench, Settings, Pencil,
  FileText, Copy, X, Info, Check, Circle,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useRole } from '../../hooks/useRole';
import { ThemedText, Button, Input, ConfirmDialog } from '../';
import { renderFormIcon, LUCIDE_ICON_MAP } from '../../utils/iconUtils';
import { IconPicker } from '../IconPicker';
import { COMPONENT_LIBRARY } from './ComponentLibrary';
import { FormFieldRenderer } from './FormFieldRenderer';
import { FieldPropertiesPanel } from './FieldPropertiesPanel';
import { AIGenerationModal } from './AIGenerationModal';
import { PreSaveEvaluationModal } from './PreSaveEvaluationModal';
import { DynamicFormRenderer } from './DynamicFormRenderer';
import { formSchemaService, aiFormSchemaService } from '../../api';
import { convertFieldsToFormSchemaDefinition, convertFormSchemaDefinitionToFields } from '../../utils/schemaConverter';

const FormBuilderContainer = styled.div`
  display: grid;
  grid-template-columns: 340px 1fr 420px;
  gap: ${props => props.theme.spacing.lg};
  height: calc(100vh - 160px);
  overflow: hidden;

  @media (max-width: 1300px) {
    grid-template-columns: 1fr;
    height: auto;
    overflow: visible;
  }
`;

const Panel = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme?.borderRadius?.lg};
  box-shadow: ${props => props.theme.shadows.md};
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border: none;

  @media (max-width: 1200px) {
    margin-bottom: ${props => props.theme.spacing.lg};
    height: 600px;
  }
`;

const PanelHeader = styled.div`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  background-color: ${props => props.theme.colors.background};
`;

const PanelContent = styled.div`
  flex: 1;
  padding: ${props => props.theme.spacing.md};
  overflow-y: auto;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.colors.border};
    border-radius: 3px;
  }
`;

const ComponentCategory = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ComponentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${props => props.theme.spacing.sm};
`;

const ComponentButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.xs};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.sm};
  min-height: 80px;
  font-size: ${props => props.theme?.typography?.sizes.small};
  font-weight: 600;
  text-align: center;
  line-height: 1.3;
  transition: all 0.2s ease;
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid transparent;
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.text};
  cursor: pointer;

  &:hover {
    border-color: ${props => props.theme.colors.tint};
    background-color: ${props => props.theme.colors.background};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.sm};
    color: ${props => props.theme.colors.tint};
  }

  &:active {
    transform: translateY(0);
  }
`;

const ComponentIcon = styled.span`
  font-size: 24px;
  margin-bottom: 4px;
`;

const FormCanvasContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  align-items: center;
  overflow-y: auto;
  position: relative;
`;

const FormCanvas = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme?.borderRadius?.lg};
  box-shadow: ${props => props.theme.shadows.lg};
  width: 100%;
  max-width: 480px; /* Mobile width */
  min-height: 800px;
  display: flex;
  flex-direction: column;
  position: relative;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const EmptyCanvas = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  text-align: center;
  color: ${props => props.theme.colors.muted};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${props => props.theme.spacing.md};
  opacity: 0.5;
`;

const FieldList = styled.div`
  padding: ${props => props.theme.spacing.md};
  flex: 1;
  overflow-y: auto;
`;

const FormHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};
  background-color: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.md};
  border: 1px solid ${props => props.theme.colors.border};
`;

const HeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  padding-bottom: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
`;

const HeaderBottomRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
`;

const FormTitleInput = styled.input`
  font-size: 20px;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  border: 1px solid transparent;
  background: transparent;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  width: 100%;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.theme.colors.backgroundSecondary};
  }

  &:focus {
    background-color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.tint};
    outline: none;
  }
`;

const FormDescriptionInput = styled.input`
  font-size: 14px;
  color: ${props => props.theme.colors.muted};
  border: 1px solid transparent;
  background: transparent;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  width: 100%;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.theme.colors.backgroundSecondary};
    color: ${props => props.theme.colors.text};
  }

  &:focus {
    background-color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.tint};
    color: ${props => props.theme.colors.text};
    outline: none;
  }
`;

const CheckboxContainer = styled.label`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  align-items: flex-start;
  cursor: pointer;
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.backgroundSecondary}40;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  margin-top: ${props => props.theme.spacing.xs};

  input[type="checkbox"] {
    margin-top: 2px;
    cursor: pointer;
  }

  &:hover {
    background-color: ${props => props.theme.colors.backgroundSecondary}60;
  }
`;

const RepeatedFormToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.isActive
    ? `${props.theme.colors.tint}15`
    : props.theme.colors.backgroundSecondary};
  border: 2px solid ${props => props.isActive
    ? props.theme.colors.tint
    : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  color: ${props => props.isActive
    ? props.theme.colors.tint
    : props.theme.colors.text};

  &:hover {
    background-color: ${props => props.isActive
      ? `${props.theme.colors.tint}25`
      : `${props.theme.colors.tint}10`};
    border-color: ${props => props.theme.colors.tint};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SampleIdBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  background-color: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.typography.sizes.small};
`;

const ToggleIcon = styled.span`
  font-size: 16px;
  display: flex;
  align-items: center;
`;

const FormMetaContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  flex: 1;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 24px;

  &:hover {
    background-color: ${props => props.theme.colors.tint}10;
    border-color: ${props => props.theme.colors.tint};
    transform: translateY(-2px);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  align-items: center;
  flex-wrap: wrap;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${props => props.theme.spacing.lg};
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.lg};
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${props => props.theme.shadows.xl};
`;

const ModalHeader = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background-color: ${props => props.theme.colors.background};
  z-index: 1;
`;

const ModalBody = styled.div`
  padding: ${props => props.theme.spacing.xl};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${props => props.theme.colors.text};
  padding: ${props => props.theme.spacing.xs};
  line-height: 1;

  &:hover {
    opacity: 0.7;
  }
`;

export const FormBuilder = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { isSuperAdmin } = useRole();
  const navigate = useNavigate();
  const { id: formId } = useParams(); // Get form ID from URL for editing
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [selectedField, setSelectedField] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showJsonDebug, setShowJsonDebug] = useState(false);
  const [formIcon, setFormIcon] = useState('clipboard-list');
  const [isIconPickerOpen, setIsIconPickerOpen] = useState(false);
  const isEditMode = Boolean(formId);
  const [isLoadingForm, setIsLoadingForm] = useState(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewFormData, setPreviewFormData] = useState({});
  const [isRepeatedForm, setIsRepeatedForm] = useState(false);
  const [hiddenFromMainLayout, setHiddenFromMainLayout] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [isSuggestingFields, setIsSuggestingFields] = useState(false);
  const [isAIGenerated, setIsAIGenerated] = useState(false);
  const [isEvalModalOpen, setIsEvalModalOpen] = useState(false);
  const [sampleIdSuggestion, setSampleIdSuggestion] = useState(null);
  const [suppressingSuggestion, setSuppressingSuggestion] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, title: '', message: '', confirmLabel: 'Confirm', onConfirm: null });
  const closeConfirm = () => setConfirmState(s => ({ ...s, isOpen: false }));
  const aiSuggestTimerRef = React.useRef(null);

  // Initialize FormKit drag and drop
  const [fieldsRef, fields, setFields] = useDragAndDrop([], {
    group: 'form-fields'
  });

  // Load existing form data if editing
  useEffect(() => {
    const loadFormData = async () => {
      if (formId) {
        setIsLoadingForm(true);
        try {
          const formData = await formSchemaService.getFormSchemaById(formId);
          console.log('Loaded form data:', formData);

          setFormName(formData.name || '');
          setFormDescription(formData.description || '');
          setFormIcon(formData.schemaDefinition?.icon || formData.icon || 'clipboard-list');
          setIsRepeatedForm(formData.isRepeatedForm || false);
          setHiddenFromMainLayout(formData.hiddenFromMainLayout || formData.isRepeatedForm || false);

          // Load schema fields using new converter
          console.log('Form data has schemaDefinition:', !!formData.schemaDefinition);
          console.log('Form data has schemaJson:', !!formData.schemaJson);

          if (formData.schemaDefinition) {
            try {
              console.log('Loading from schemaDefinition:', formData.schemaDefinition);
              const loadedFields = convertFormSchemaDefinitionToFields(formData.schemaDefinition);
              console.log('Successfully converted fields:', loadedFields);
              setFields(ensureUniqueFieldNames(loadedFields));
            } catch (parseError) {
              console.error('Failed to convert schema definition:', parseError);
              console.error('Schema definition value:', formData.schemaDefinition);
              toast.error('Failed to load form structure: ' + parseError.message);
            }
          } else if (formData.schemaJson) {
            // Backward compatibility: handle old schemaJson format
            try {
              console.log('Loading from legacy schemaJson (first 200 chars):', formData.schemaJson.substring(0, 200));
              const schema = JSON.parse(formData.schemaJson);
              console.log('Parsed schema:', schema);
              const loadedFields = convertFormSchemaDefinitionToFields(schema);
              console.log('Successfully converted legacy fields:', loadedFields);
              setFields(ensureUniqueFieldNames(loadedFields));
            } catch (parseError) {
              console.error('Failed to parse schema JSON:', parseError);
              console.error('SchemaJson value (first 500 chars):', formData.schemaJson?.substring(0, 500));
              toast.error('Failed to load form structure: ' + parseError.message);
            }
          } else {
            console.warn('No schema data found in form response');
            toast.error('Form has no schema data');
          }
        } catch (error) {
          console.error('Failed to load form:', error);
          toast.error('Failed to load form for editing');
        } finally {
          setIsLoadingForm(false);
        }
      }
    };

    loadFormData();
  }, [formId]);

  // Check URL params for isRepeatedForm / hiddenFromMainLayout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('isRepeatedForm') === 'true' || params.get('hiddenFromMainLayout') === 'true') {
      setIsRepeatedForm(true);
      setHiddenFromMainLayout(true);
    }
  }, []);

  // AI suggestions: debounce fetch when fields change (min 2 fields, only if form has a name)
  useEffect(() => {
    if (aiSuggestTimerRef.current) clearTimeout(aiSuggestTimerRef.current);
    if (fields.length < 1 || !formName.trim()) return;

    aiSuggestTimerRef.current = setTimeout(async () => {
      try {
        setIsSuggestingFields(true);
        const schema = convertFieldsToFormSchemaDefinition(fields, formName, formDescription, formIcon);
        const suggestions = await aiFormSchemaService.suggestNextFields({
          currentSchema: schema,
          formTitle: formName,
        });
        setAiSuggestions(suggestions || []);
      } catch {
        // silent — suggestions are optional
      } finally {
        setIsSuggestingFields(false);
      }
    }, 4000); // 4-second debounce

    return () => clearTimeout(aiSuggestTimerRef.current);
  }, [fields.length, formName]); // eslint-disable-line

  // Generate unique ID for new fields
  const generateId = () => `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Map AI suggestion field type → ComponentLibrary id
  const AI_TYPE_TO_COMPONENT_ID = {
    Text: 'input', TextArea: 'textarea', Number: 'number', Select: 'dropdown',
    Radio: 'radio', CheckboxGroup: 'checkbox_group', Toggle: 'toggle',
    Date: 'date', Time: 'time', Location: 'geolocation', Image: 'camera',
    Drawing: 'drawing', Tags: 'tags', QRCode: 'qr_scanner', Barcode: 'barcode_scanner',
    RepeatedForm: 'repeated_form', Header: 'header', Divider: 'divider',
  };

  const addSuggestedField = (suggestion) => {
    const componentId = AI_TYPE_TO_COMPONENT_ID[suggestion.type] || 'input';
    const component = COMPONENT_LIBRARY.find(c => c.id === componentId);
    if (!component) return;

    const newField = {
      id: generateId(),
      type: component.type,
      props: {
        ...component.defaultProps,
        label: suggestion.title,
        name: suggestion.key || generateFieldName(suggestion.title),
        required: suggestion.required || false,
        placeholder: suggestion.placeholder || component.defaultProps?.placeholder || '',
        ...(suggestion.options?.length > 0 ? {
          options: suggestion.options.map(o => ({ value: o.toLowerCase().replace(/\s+/g, '_'), label: o }))
        } : {}),
      },
    };

    setFields(prev => ensureUniqueFieldNames([...prev, newField]));
    setAiSuggestions(prev => prev.filter(s => s.key !== suggestion.key));
    toast.success(`Added "${suggestion.title}" field`);
  };

  // Generate field name from label
  const generateFieldName = (label) => {
    const name = label.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
    return name || 'field';
  };

  // Ensure unique field names across all fields
  const ensureUniqueFieldNames = (fieldsList) => {
    const usedNames = new Set();

    return fieldsList.map(field => {
      let fieldName = field.props.name || field.id;
      let counter = 1;
      const originalName = fieldName;

      // Add numbering if name already exists
      while (usedNames.has(fieldName)) {
        fieldName = `${originalName}_${counter}`;
        counter++;
      }

      usedNames.add(fieldName);

      // Update the field's actual name if it changed
      if (fieldName !== field.props.name) {
        return {
          ...field,
          props: {
            ...field.props,
            name: fieldName
          }
        };
      }

      return field;
    });
  };

  // Add field to form
  const addField = (componentType) => {
    const component = COMPONENT_LIBRARY.find(c => c.id === componentType);
    if (!component) return;

    // Check if trying to add geolocation when one already exists
    if (component.type === 'geolocation') {
      const existingGeoField = fields.find(field => field.type === 'geolocation');
      if (existingGeoField) {
        toast.error('Only one location field is allowed per form');
        return;
      }
    }

    // Check if trying to add repeated_form when this form is marked as repeated
    if (component.type === 'repeated_form' && (isRepeatedForm || hiddenFromMainLayout)) {
      toast.error('Forms hidden from main layout cannot contain repeated form components');
      return;
    }

    const initialProps = JSON.parse(JSON.stringify(component.defaultProps));

    // Auto-generate field name from label
    if (initialProps.label) {
      initialProps.name = generateFieldName(initialProps.label);
      initialProps.nameManuallySet = false;
    } else {
      initialProps.nameManuallySet = true;
    }

    const newField = {
      id: generateId(),
      type: component.type,
      componentId: component.id,
      props: initialProps
    };

    // Ensure unique field names
    const updatedFields = ensureUniqueFieldNames([...fields, newField]);
    setFields(updatedFields);

    // Select the new field
    const addedField = updatedFields[updatedFields.length - 1];
    setSelectedField(addedField);
    toast.success(`${component.label} added to form`);
  };

  // Update field properties
  const updateField = (updatedField) => {
    let newFields = fields.map(field =>
      field.id === updatedField.id ? updatedField : field
    );

    // Enforce only one isSampleId field — clear it from other fields if this one just enabled it
    if (updatedField.props.isSampleId) {
      const previous = fields.find(f => f.id !== updatedField.id && f.props.isSampleId);
      if (previous) {
        toast.info(`"${previous.props.label}" is no longer the Sample ID field.`);
      }
      newFields = newFields.map(field =>
        field.id !== updatedField.id
          ? { ...field, props: { ...field.props, isSampleId: false } }
          : field
      );
    }

    // Ensure unique field names after update
    const fieldsWithUniqueNames = ensureUniqueFieldNames(newFields);
    setFields(fieldsWithUniqueNames);

    // Update selected field reference
    const updatedFieldAfterNameCheck = fieldsWithUniqueNames.find(f => f.id === updatedField.id);
    setSelectedField(updatedFieldAfterNameCheck);
  };

  // Delete field
  const deleteField = (fieldId) => {
    setFields(fields.filter(field => field.id !== fieldId));
    if (selectedField?.id === fieldId) {
      setSelectedField(null);
    }
    toast.success('Field removed from form');
  };

  // Generate form schema definition using new converter
  const generateSchema = () => {
    return convertFieldsToFormSchemaDefinition(
      fields,
      formName || 'Untitled Form',
      formDescription || 'Form created with aQRate FormBuilder',
      formIcon
    );
  };

  // Actual save logic — accepts an optional schemaDefinition override (for AI-refined schemas)
  const performSave = async (schemaDefinitionOverride) => {
    if (!formName.trim()) {
      toast.error('Please enter a form name');
      return;
    }

    if (fields.length === 0) {
      toast.error('Please add at least one field to the form');
      return;
    }

    setIsEvalModalOpen(false);
    setIsSaving(true);
    try {
      const schemaDefinition = schemaDefinitionOverride || generateSchema();

      // When saving with AI refinements, use name/description/icon from the schema
      // since React state updates from handleApplyAISchema haven't flushed yet
      const effectiveName = schemaDefinition.title || formName;
      const effectiveDesc = schemaDefinition.description || formDescription;
      const schemaIcon = schemaDefinition.icon && LUCIDE_ICON_MAP[schemaDefinition.icon]
        ? schemaDefinition.icon : null;
      const effectiveIcon = schemaIcon || (LUCIDE_ICON_MAP[formIcon] ? formIcon : 'clipboard-list');

      // Ensure schemaDefinition icon is valid
      if (schemaDefinition.icon && !LUCIDE_ICON_MAP[schemaDefinition.icon]) {
        schemaDefinition.icon = 'clipboard-list';
      }

      const formData = {
        name: effectiveName.trim(),
        description: (effectiveDesc || '').trim() || `${effectiveName} form`,
        schemaDefinition: schemaDefinition,
        icon: effectiveIcon,
        isPublic: false,
        isRepeatedForm: isRepeatedForm,
        hiddenFromMainLayout: hiddenFromMainLayout
      };

      if (isEditMode && formId) {
        // Update existing form
        await formSchemaService.updateFormSchema(formId, {
          ...formData,
          isActive: true
        });
        toast.success('Form updated successfully!');

        // Check if the form should have a Sample ID field
        try {
          const sugResult = await formSchemaService.getFormSchemaSuggestions(formId);
          const sugData = sugResult?.data || sugResult;
          if (sugData?.shouldSuggestSampleId) {
            setSampleIdSuggestion(sugData);
          } else {
            setSampleIdSuggestion(null);
          }
        } catch { /* non-critical */ }
      } else {
        // Create new form
        await formSchemaService.createFormSchema(formData);
        toast.success('Form created successfully!');
      }

      // Ask user if they want to go to forms list or continue editing
      setConfirmState({
        isOpen: true,
        title: 'Form Saved',
        message: 'Form saved! Would you like to view all forms?',
        confirmLabel: 'View All Forms',
        cancelLabel: 'Continue Editing',
        onConfirm: () => {
          closeConfirm();
          navigate('/forms');
        },
        onCancel: () => {
          closeConfirm();
          if (!isEditMode) {
            // If creating new, clear form for creating another
            setFields([]);
            setSelectedField(null);
            setFormName('');
            setFormDescription('');
            setFormIcon('clipboard-list');
            setIsAIGenerated(false);
          }
        },
      });
    } catch (error) {
      console.error('Failed to save form:', error);
      console.error('Save payload was:', { formName, fieldCount: fields.length, isEditMode, formId });
      if (error.response) {
        console.error('Server response:', error.response.status, error.response.data);
      }
      toast.error('Failed to save form: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  // Save button handler — always open evaluation modal for consistent UX
  const handleSaveClick = () => {
    if (!formName.trim()) {
      toast.error('Please enter a form name');
      return;
    }
    if (fields.length === 0) {
      toast.error('Please add at least one field to the form');
      return;
    }
    setIsEvalModalOpen(true);
  };

  // Called from PreSaveEvaluationModal when user wants to apply AI refinements then save
  const handleApplyAndSave = (refinedResult) => {
    handleApplyAISchema(refinedResult);
    performSave(refinedResult.schemaDefinition);
  };

  // Called from PreSaveEvaluationModal when user wants to apply AI refinements without saving
  const handleApply = (refinedResult) => {
    handleApplyAISchema(refinedResult);
    setIsEvalModalOpen(false);
  };

  // Clear form
  const handleClearForm = () => {
    setConfirmState({
      isOpen: true,
      title: 'Clear Form',
      message: 'Are you sure you want to clear the current form? This cannot be undone.',
      confirmLabel: 'Clear',
      onConfirm: () => {
        closeConfirm();
        setFields([]);
        setSelectedField(null);
        setFormName('');
        setFormDescription('');
        setFormIcon('clipboard-list');
        setIsAIGenerated(false);
        toast.info('Form cleared');
      },
    });
  };

  // Handle toggling repeated form status with cleanup
  const handleRepeatedFormToggle = (newValue) => {
    if (newValue === true) {
      // Check if there are any repeated_form components
      const repeatedFormFields = fields.filter(field => field.type === 'repeated_form');

      if (repeatedFormFields.length > 0) {
        // Show warning and ask for confirmation
        const message = `This form contains ${repeatedFormFields.length} repeated form component${repeatedFormFields.length > 1 ? 's' : ''}. ` +
                       `These will be removed when you mark this form as a repeated form template. ` +
                       `Do you want to continue?`;

        setConfirmState({
          isOpen: true,
          title: 'Remove Repeated Form Components',
          message,
          confirmLabel: 'Continue',
          onConfirm: () => {
            closeConfirm();
            // Remove all repeated_form components
            const cleanedFields = fields.filter(field => field.type !== 'repeated_form');
            setFields(cleanedFields);

            // Clear selection if selected field was removed
            if (selectedField && selectedField.type === 'repeated_form') {
              setSelectedField(null);
            }

            setIsRepeatedForm(true);
            setHiddenFromMainLayout(true);
            toast.success(`Removed ${repeatedFormFields.length} repeated form component${repeatedFormFields.length > 1 ? 's' : ''}`);
          },
        });
        // If user cancels, don't change the state
      } else {
        // No repeated_form components, just toggle
        setIsRepeatedForm(true);
        setHiddenFromMainLayout(true);
      }
    } else {
      // Turning OFF - no cleanup needed
      setIsRepeatedForm(false);
      setHiddenFromMainLayout(false);
    }
  };

  // Handle icon selection
  const handleIconSelect = (icon) => {
    setFormIcon(icon);
    toast.success(`Form icon updated to ${icon}`);
  };

  // Handle AI-generated schema application
  const handleApplyAISchema = (generationResult) => {
    try {
      // Apply suggested metadata — check both AI generation props and schema definition props
      const newName = generationResult.suggestedName || generationResult.schemaDefinition?.title;
      if (newName) {
        setFormName(newName);
      }
      const newDesc = generationResult.suggestedDescription || generationResult.schemaDefinition?.description;
      if (newDesc) {
        setFormDescription(newDesc);
      }

      // Apply icon from schemaDefinition — only if it's a valid Lucide icon name
      // (AI may return emoji icons; ignore those since the app uses Lucide icons)
      if (generationResult.schemaDefinition?.icon) {
        const aiIcon = generationResult.schemaDefinition.icon;
        if (LUCIDE_ICON_MAP[aiIcon]) {
          setFormIcon(aiIcon);
        }
      }

      // Convert schema definition to fields
      if (generationResult.schemaDefinition) {
        const newFields = convertFormSchemaDefinitionToFields(generationResult.schemaDefinition);
        const uniqueFields = ensureUniqueFieldNames(newFields);
        setFields(uniqueFields);
        setIsAIGenerated(true);
        toast.success(`Successfully loaded ${newFields.length} AI-generated fields!`);
      }

      // Show validation warnings if any
      if (generationResult.validationResult?.warnings?.length > 0) {
        generationResult.validationResult.warnings.forEach((warning) => {
          toast.warning(warning.message, { autoClose: 5000 });
        });
      }
    } catch (error) {
      console.error('Failed to apply AI schema:', error);
      toast.error('Failed to apply generated schema: ' + error.message);
    }
  };

  // Group components by category
  // Filter out repeated_form component when this form is marked as repeated
  const componentsByCategory = COMPONENT_LIBRARY
    .filter(component => !(component.type === 'repeated_form' && (isRepeatedForm || hiddenFromMainLayout)))
    .reduce((acc, component) => {
      if (!acc[component.category]) acc[component.category] = [];
      acc[component.category].push(component);
      return acc;
    }, {});

  if (isLoadingForm) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <ThemedText>Loading form data...</ThemedText>
      </div>
    );
  }

  return (
    <div>
      <FormHeader theme={theme}>
        {/* Top Row: Icon, Title, and Actions */}
        <HeaderTopRow theme={theme}>
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, flex: 1 }}>
            <IconButton
              theme={theme}
              onClick={() => setIsIconPickerOpen(true)}
              title="Choose form icon"
            >
              {renderFormIcon(formIcon, 24)}
            </IconButton>

            <FormTitleInput
              placeholder="Untitled Form"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              theme={theme}
              style={{ fontSize: '24px', height: '48px' }}
            />
          </div>

          <ActionButtons theme={theme}>
            <Button
              variant="secondary" // Changed to secondary to not compete with save
              onClick={() => setIsAIModalOpen(true)}
              title="Generate form schema with AI"
              size="sm"
            >
              <Sparkles size={14} /> AI Gen
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                if (fields.length === 0) {
                  toast.warning('Please add at least one field to preview');
                  return;
                }
                setPreviewFormData({});
                setIsPreviewOpen(true);
              }}
              title="Preview how the form will look to users"
              size="sm"
            >
              <Smartphone size={14} /> Preview
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveClick}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : <><Save size={14} /> Save Form</>}
            </Button>
            {isSuperAdmin() && (
              <Button
                variant={showJsonDebug ? "primary" : "secondary"}
                onClick={() => setShowJsonDebug(!showJsonDebug)}
                title="Toggle JSON debug view (SuperAdmin only)"
                size="sm"
              >
                <Wrench size={14} /> JSON
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={handleClearForm}
              size="sm"
              style={{ opacity: 0.7 }}
            >
              <Trash2 size={14} />
            </Button>
          </ActionButtons>
        </HeaderTopRow>

        {/* Bottom Row: Description and Settings */}
        <HeaderBottomRow theme={theme}>
          <div style={{ flex: 2 }}>
            <FormDescriptionInput
              placeholder="Add a description..."
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              theme={theme}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <RepeatedFormToggleButton
              theme={theme}
              isActive={hiddenFromMainLayout}
              onClick={() => handleRepeatedFormToggle(!hiddenFromMainLayout)}
              type="button"
              title={hiddenFromMainLayout
                ? "This form is hidden from the main layout"
                : "Click to hide this form from the main layout"}
            >
              <ToggleIcon>
                {hiddenFromMainLayout ? <Check size={12} /> : <Circle size={12} />}
              </ToggleIcon>
              <ThemedText type="small" style={{ fontWeight: 600 }}>
                Hidden from Main Layout
              </ThemedText>
            </RepeatedFormToggleButton>
            {isEditMode && (
              <ThemedText type="small" style={{ color: theme.colors.muted, marginLeft: theme.spacing.md }}>
                <Pencil size={12} /> Editing
              </ThemedText>
            )}
          </div>
        </HeaderBottomRow>
      </FormHeader>

      {/* Sample ID suggestion banner */}
      {sampleIdSuggestion && (
        <SampleIdBanner theme={theme}>
          <Info size={16} style={{ color: '#92400e', flexShrink: 0, marginTop: 1 }} />
          <div style={{ flex: 1 }}>
            <strong style={{ color: '#92400e' }}>Consider adding a Sample ID field</strong>
            <div style={{ color: '#78350f', marginTop: 2 }}>
              {sampleIdSuggestion.reason || 'This form looks like it\'s used for field sampling. Marking one field as the Sample ID enables lab reports to be auto-linked.'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button
              onClick={async () => {
                setSuppressingSuggestion(true);
                try {
                  await formSchemaService.suppressSampleIdSuggestion(formId);
                  setSampleIdSuggestion(null);
                } catch { setSampleIdSuggestion(null); }
                finally { setSuppressingSuggestion(false); }
              }}
              disabled={suppressingSuggestion}
              style={{ background: 'none', border: '1px solid #fcd34d', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#92400e' }}
            >
              {suppressingSuggestion ? '...' : "Don't show again"}
            </button>
            <button
              onClick={() => setSampleIdSuggestion(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400e', padding: '4px 6px' }}
              title="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        </SampleIdBanner>
      )}

      <FormBuilderContainer theme={theme}>
        {/* Component Toolbox */}
        <Panel theme={theme}>
          <PanelHeader theme={theme}>
            <ThemedText type="defaultSemiBold">
              <Wrench size={14} /> Components
            </ThemedText>
            <ThemedText type="small" style={{ color: theme.colors.muted }}>
              {Object.values(componentsByCategory).flat().length} mobile-optimized fields
            </ThemedText>
          </PanelHeader>
          <PanelContent theme={theme}>
            {/* AI Field Suggestions — shown at the top so they're immediately visible */}
            {(aiSuggestions.length > 0 || isSuggestingFields) && (
              <ComponentCategory theme={theme} style={{ borderBottom: `1px solid ${theme.colors.border}`, paddingBottom: theme.spacing.md, marginBottom: theme.spacing.sm }}>
                <ThemedText
                  type="defaultSemiBold"
                  style={{ marginBottom: theme.spacing.sm, fontSize: theme.typography.sizes.default, color: theme.colors.tint, fontWeight: '700' }}
                >
                  <Sparkles size={14} /> {isSuggestingFields ? 'AI Thinking...' : 'AI Suggestions'}
                </ThemedText>
                {isSuggestingFields ? (
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>Analyzing your form...</ThemedText>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                    {aiSuggestions.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => addSuggestedField(s)}
                        title={s.reason}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6, padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                          background: `${theme.colors.tint}15`, border: `1px dashed ${theme.colors.tint}60`,
                          borderRadius: theme.borderRadius.sm, cursor: 'pointer', textAlign: 'left', width: '100%',
                          color: theme.colors.text, fontSize: theme.typography.sizes.small,
                        }}
                      >
                        <Sparkles size={12} style={{ color: theme.colors.tint, flexShrink: 0 }} />
                        <span style={{ fontWeight: 600 }}>+ {s.title}</span>
                        <span style={{ color: theme.colors.muted, fontSize: '11px' }}>({s.type})</span>
                      </button>
                    ))}
                  </div>
                )}
              </ComponentCategory>
            )}

            {Object.entries(componentsByCategory).map(([category, components]) => (
              <ComponentCategory key={category} theme={theme}>
                <ThemedText
                  type="defaultSemiBold"
                  style={{
                    marginBottom: theme.spacing.md,
                    fontSize: theme.typography.sizes.default,
                    color: theme.colors.text,
                    fontWeight: '700'
                  }}
                >
                  {category === 'inputs' ? <><Pencil size={14} /> Form Fields</> : <><Settings size={14} /> Layout</>}
                </ThemedText>
                <ComponentGrid theme={theme}>
                  {components.map(component => (
                    <ComponentButton
                      key={component.id}
                      onClick={() => addField(component.id)}
                      title={`Add ${component.label}`}
                      theme={theme}
                    >
                      <ComponentIcon>{component.icon}</ComponentIcon>
                      <span style={{ fontSize: '12px', fontWeight: '600' }}>
                        {component.label}
                      </span>
                    </ComponentButton>
                  ))}
                </ComponentGrid>
              </ComponentCategory>
            ))}

          </PanelContent>
        </Panel>

        {/* Form Canvas */}
        <FormCanvasContainer theme={theme}>
          <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.md }}>
            <Smartphone size={14} /> Mobile Preview
          </ThemedText>
          <FormCanvas theme={theme}>
            <PanelHeader theme={theme}>
              <ThemedText type="defaultSemiBold">
                {formName || 'Untitled Form'}
              </ThemedText>
              <ThemedText type="small" style={{ color: theme.colors.muted }}>
                {formDescription || 'No description'}
              </ThemedText>
            </PanelHeader>

            {fields.length === 0 ? (
              <EmptyCanvas theme={theme}>
                <EmptyIcon><FileText size={32} /></EmptyIcon>
                <ThemedText type="subtitle">Start Building</ThemedText>
                <ThemedText style={{ color: theme.colors.muted, maxWidth: '200px' }}>
                  Drag components here from the left panel
                </ThemedText>
              </EmptyCanvas>
            ) : (
              <FieldList ref={fieldsRef}>
                {(() => {
                  let sectionHeaderRendered = false;
                  return fields.map((field) => {
                    const isLabResult = !!field.props?.labResultSource;
                    const sectionStart = isLabResult && !sectionHeaderRendered;
                    if (sectionStart) sectionHeaderRendered = true;
                    return (
                      <FormFieldRenderer
                        key={field.id}
                        field={field}
                        isSelected={selectedField?.id === field.id}
                        onSelect={setSelectedField}
                        onUpdate={updateField}
                        onDelete={deleteField}
                        deemphasized={isLabResult}
                        sectionStart={sectionStart}
                      />
                    );
                  });
                })()}
              </FieldList>
            )}
          </FormCanvas>
        </FormCanvasContainer>

        {/* Properties Panel */}
        <Panel theme={theme}>
          <PanelContent theme={theme} style={{ padding: 0 }}>
            <FieldPropertiesPanel
              field={selectedField}
              onUpdate={updateField}
              generateFieldName={generateFieldName}
              allFields={fields}
              fields={fields}
              currentFormId={formId ? parseInt(formId, 10) : null}
              onFieldsReplace={(nextFields) => {
                const unique = ensureUniqueFieldNames(nextFields);
                setFields(unique);
                const selectedAfter = unique.find(f => f.id === selectedField?.id);
                if (selectedAfter) setSelectedField(selectedAfter);
              }}
            />
          </PanelContent>
        </Panel>
      </FormBuilderContainer>

      {/* Schema Preview */}
      {fields.length > 0 && showJsonDebug && (
        <div style={{ marginTop: theme.spacing.xl }}>
          <Panel theme={theme}>
            <PanelHeader theme={theme}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <ThemedText type="defaultSemiBold">
                    <Wrench size={14} /> Generated Form Schema
                  </ThemedText>
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    JSON schema for backend integration
                  </ThemedText>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    const schema = JSON.stringify(generateSchema(), null, 2);
                    navigator.clipboard.writeText(schema).then(() => {
                      toast.success('Schema copied to clipboard!');
                    }).catch(() => {
                      toast.error('Failed to copy to clipboard');
                    });
                  }}
                >
                  <Copy size={14} /> Copy
                </Button>
              </div>
            </PanelHeader>
            <PanelContent theme={theme}>
              <pre style={{
                backgroundColor: theme.colors.background,
                padding: theme.spacing.md,
                borderRadius: theme?.borderRadius?.md,
                border: `1px solid ${theme.colors.border}`,
                fontSize: '12px',
                maxHeight: '300px',
                overflow: 'auto',
                whiteSpace: 'pre-wrap'
              }}>
                {JSON.stringify(generateSchema(), null, 2)}
              </pre>
            </PanelContent>
          </Panel>
        </div>
      )}

      {/* Icon Picker Modal */}
      <IconPicker
        isOpen={isIconPickerOpen}
        onClose={() => setIsIconPickerOpen(false)}
        onSelect={handleIconSelect}
        selectedIcon={formIcon}
      />

      {/* AI Generation Modal */}
      <AIGenerationModal
        isOpen={isAIModalOpen}
        onClose={() => setIsAIModalOpen(false)}
        onApply={handleApplyAISchema}
        currentFields={fields}
      />

      {/* Pre-Save Evaluation Modal */}
      <PreSaveEvaluationModal
        isOpen={isEvalModalOpen}
        onClose={() => setIsEvalModalOpen(false)}
        onSave={() => performSave()}
        onApply={handleApply}
        onApplyAndSave={handleApplyAndSave}
        currentSchema={generateSchema()}
        formName={formName}
        fieldCount={fields.length}
      />

      {/* Form Preview Modal */}
      <Modal theme={theme} isOpen={isPreviewOpen}>
        <ModalContent theme={theme}>
          <ModalHeader theme={theme}>
            <div>
              <ThemedText type="subtitle">
                {renderFormIcon(formIcon, 20)} {formName || 'Untitled Form'} - Preview
              </ThemedText>
              <ThemedText type="small" style={{ color: theme.colors.muted, marginTop: theme.spacing.xs }}>
                {formDescription || 'No description'}
              </ThemedText>
            </div>
            <CloseButton theme={theme} onClick={() => setIsPreviewOpen(false)}>
              <X size={16} />
            </CloseButton>
          </ModalHeader>
          <ModalBody theme={theme}>
            <ThemedText type="small" style={{
              color: theme.colors.muted,
              marginBottom: theme.spacing.lg,
              display: 'block',
              padding: theme.spacing.sm,
              backgroundColor: theme.colors.backgroundSecondary,
              borderRadius: theme.borderRadius.sm
            }}>
              <Info size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> This is a preview of how your form will look to users. Changes made here will not be saved.
            </ThemedText>
            <DynamicFormRenderer
              schema={fields}
              formData={previewFormData}
              onChange={setPreviewFormData}
              errors={{}}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <ConfirmDialog
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        confirmLabel={confirmState.confirmLabel}
        cancelLabel={confirmState.cancelLabel}
        onConfirm={confirmState.onConfirm}
        onCancel={confirmState.onCancel || closeConfirm}
      />
    </div>
  );
};